import {useCallback, useMemo, useState} from 'react';

import {addQueryToPath} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {
  useBuildCancelToken,
  useDebounce,
} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CancelError, CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {useAppSelector} from '../../../src/helpers/appReduxHelpers';
import type {SearchInConversationResponseModel} from '../../../src/models/api/searchInConversationMatchModel';
import {ApiSearchInConversationMatch} from '../../../src/models/api/searchInConversationMatchModel';
import {getCompany} from '../../../src/store/data/company/companySelectors';

const SEARCH_IN_CONVERSATION_DEBBOUNCE_WAIT_MS = 500;

export function useSearchInConversation(query: string | undefined, conversationId: number) {
  const sanitizedQuery = useMemo(() => (query ? query.trim().replace(/\s+/g, ' ') : ''), [query]);
  const [isLoading, setIsLoading] = useState(false);
  const {buildCancelToken} = useBuildCancelToken();

  // Suggestions.
  const company = useAppSelector(getCompany);

  const [matchesInConversation, setMatchesInConversation] = useState<
    SearchInConversationResponseModel['matchesInConversation']
  >([]);

  const fetchSearchInConversationAsync = useCallback(
    async (cancelToken?: CancelToken) => {
      let newMatches: SearchInConversationResponseModel['matchesInConversation'] = [];
      try {
        if (sanitizedQuery && conversationId) {
          const items = await fetchSearchInConversation(sanitizedQuery, conversationId, company, cancelToken);
          newMatches = [...items];
          setMatchesInConversation(newMatches);
        }
      } catch (e) {
        if (!(e instanceof CancelError)) {
          throw e;
        }
      } finally {
        setIsLoading(false);
      }
      return newMatches;
    },
    [company, conversationId, sanitizedQuery, setIsLoading, setMatchesInConversation],
  );

  const fetchSuggestionsAsyncDebounced = useDebounce(
    fetchSearchInConversationAsync,
    SEARCH_IN_CONVERSATION_DEBBOUNCE_WAIT_MS,
  );

  const refetchActivityIds = useCallback(() => {
    setIsLoading(true);
    fetchSuggestionsAsyncDebounced(buildCancelToken());
  }, [fetchSuggestionsAsyncDebounced, setIsLoading, buildCancelToken]);

  return {
    matchesInConversation,
    refetchActivityIds,
    isLoading,
  };
}

async function fetchSearchInConversation(
  query: string,
  conversationId: number,
  company: CompanyModel,
  cancelToken?: CancelToken | undefined,
) {
  const encodedQuery = encodeURIComponent(query);
  const path = addQueryToPath(`${company.url}/search_in_conversation/${encodedQuery}`, {
    conversation_id: conversationId,
  });

  const searchInConversationResults = await fetchApiAsync(ApiSearchInConversationMatch, path, {cancelToken});

  return searchInConversationResults.matchesInConversation;
}
